/**
 * Build Excel sheet specs from runtime `ObjectType` definitions.
 *
 * The single source of truth for column layout. Both exporter and parser
 * reuse the same spec so headers stay aligned.
 *
 * Column rules (per design §3.3):
 *   1. Always include `id` (required, identity).
 *   2. Always include `name` (required, source: 'name').
 *   3. Include each `objectType.properties[].key` whose key is NOT in
 *      `EXCLUDED_FIELD_KEYS` and is NOT a relationship array (`linkedXxxIds`).
 *   4. Look up the corresponding zod field on the static export schema
 *      registry (best-effort) for stricter type validation.
 *   5. The first column is always `id`.
 */

import type { z } from 'zod'
import * as exportSchema from '../../../map-editor-entities/schema/exportSchema'
import type { ObjectType, Property } from '../../../map-editor-shared/store/objectTypeStore'
import {
  EXCLUDED_FIELD_KEYS,
  isRelationshipArrayKey,
} from './constants'
import type {
  ExcelColumn,
  ExcelColumnPrimitive,
  ExcelSheetSpec,
} from '../model/types'

// Excel sheet name rules: max 31 chars, may not contain : \ / ? * [ ],
// may not start/end with apostrophe, may not be the reserved word "History".
const EXCEL_SHEET_NAME_FORBIDDEN_RE = /[:\\/?*\[\]]/g
const EXCEL_SHEET_NAME_MAX_LENGTH = 31

/**
 * Sanitize an ObjectType.name into a valid xlsx sheet name. Used by both
 * exporter (write) and parser (lookup) so a sanitized export can be re-imported.
 */
export function sanitizeSheetName(name: string): string {
  let safe = name.replace(EXCEL_SHEET_NAME_FORBIDDEN_RE, '_').trim()
  // Collapse runs of underscores produced by stripping multiple bad chars,
  // then trim boundary underscores so e.g. "a[b]" → "a_b" (not "a_b_").
  safe = safe.replace(/_+/g, '_').replace(/^_+|_+$/g, '')
  // Apostrophes are allowed mid-name but not at the boundary.
  safe = safe.replace(/^'+|'+$/g, '')
  if (safe.length === 0) safe = 'Sheet'
  if (safe.length > EXCEL_SHEET_NAME_MAX_LENGTH) {
    safe = safe.slice(0, EXCEL_SHEET_NAME_MAX_LENGTH)
  }
  if (safe.toLowerCase() === 'history') safe = 'History_'
  return safe
}

/** Mapping from ObjectType.name (case-insensitive) to a known zod ObjectSchema. */
const SHEET_NAME_TO_SCHEMA: Record<string, z.ZodObject<z.ZodRawShape> | undefined> = {
  cctv: exportSchema.LightCCTVSchema,
  lightcctv: exportSchema.LightCCTVSchema,
  zone: exportSchema.ZoneSchema,
  column: exportSchema.ColumnSchema,
  parkinglocation: exportSchema.ParkingLocationSchema,
  parkingspot: exportSchema.ParkingLocationSchema,
  sensor: exportSchema.SensorSchema,
  charger: exportSchema.ChargerSchema,
  guideboard: exportSchema.GuideBoardSchema,
  elevator: exportSchema.ElevatorSchema,
  emergencybell: exportSchema.EmergencyBellSchema,
  arrow: exportSchema.ArrowSchema,
  line: exportSchema.LineSchema,
  outline: exportSchema.LineSchema,
  innerline: exportSchema.LineSchema,
  entrance: exportSchema.EntranceSchema,
  onepassreader: exportSchema.OnePassReaderSchema,
  occupancylight: exportSchema.OccupancyLightSchema,
  light: exportSchema.LightSchema,
}

/** Look up the zod schema for an `ObjectType.name`, ignoring case/whitespace/dashes. */
export function findSchemaByObjectTypeName(
  name: string
): z.ZodObject<z.ZodRawShape> | undefined {
  const normalized = name.toLowerCase().replace(/[\s_-]/g, '')
  return SHEET_NAME_TO_SCHEMA[normalized]
}

/** Look up a single field's zod inside a known schema, by camelCase key. */
function findZodFieldOnSchema(
  schema: z.ZodObject<z.ZodRawShape> | undefined,
  key: string
): z.ZodTypeAny | undefined {
  if (!schema) return undefined
  // ZodObject.shape exposes the raw shape map.
  const shape = schema.shape as Record<string, z.ZodTypeAny>
  return shape[key]
}

/** Map ObjectType.Property.type → ExcelColumnPrimitive. */
function propertyTypeToPrimitive(t: Property['type']): ExcelColumnPrimitive {
  switch (t) {
    case 'number':
    case 'boolean':
    case 'array':
      return t
    default:
      return 'string'
  }
}

/** Filter out excluded / unsafe property keys for Phase 1. */
function isPropertyExportable(prop: Property): boolean {
  if (EXCLUDED_FIELD_KEYS.includes(prop.key)) return false
  if (isRelationshipArrayKey(prop.key)) return false
  return true
}

/**
 * Build the Excel sheet spec for a single ObjectType.
 * Returns columns in stable order: [id, name, ...sorted properties by key].
 */
export function buildSheetSpec(objectType: ObjectType): ExcelSheetSpec {
  const schema = findSchemaByObjectTypeName(objectType.name)

  const idColumn: ExcelColumn = {
    key: 'id',
    header: 'id',
    required: true,
    primitive: 'string',
    zodField: findZodFieldOnSchema(schema, 'id'),
    source: 'identity',
  }

  const nameColumn: ExcelColumn = {
    key: 'name',
    header: 'name',
    required: true,
    primitive: 'string',
    zodField: findZodFieldOnSchema(schema, 'name'),
    source: 'name',
  }

  const propertyColumns: ExcelColumn[] = objectType.properties
    .filter(isPropertyExportable)
    // Stable sort by key — keeps Excel files diff-able across runs.
    .slice()
    .sort((a, b) => a.key.localeCompare(b.key))
    .map<ExcelColumn>(prop => ({
      key: prop.key,
      header: prop.key,
      required: prop.required,
      primitive: propertyTypeToPrimitive(prop.type),
      zodField: findZodFieldOnSchema(schema, prop.key),
      source: 'property',
    }))

  return {
    sheetName: sanitizeSheetName(objectType.name),
    typeId: objectType.id,
    columns: [idColumn, nameColumn, ...propertyColumns],
  }
}

/**
 * Build sheet specs for all object types.
 * Skips object types with empty / blank names (defensive).
 */
export function buildAllSheetSpecs(objectTypes: ObjectType[]): ExcelSheetSpec[] {
  const used = new Set<string>()
  return objectTypes
    .filter(t => t.name.trim().length > 0)
    .map(buildSheetSpec)
    .map(spec => {
      // Disambiguate when sanitization collapses two names to the same string.
      // e.g. "parking/spot" and "parking\spot" both → "parking_spot".
      let unique = spec.sheetName
      let suffix = 2
      while (used.has(unique.toLowerCase())) {
        const tag = `_${suffix++}`
        unique = (spec.sheetName + tag).slice(0, EXCEL_SHEET_NAME_MAX_LENGTH)
      }
      used.add(unique.toLowerCase())
      return unique === spec.sheetName ? spec : { ...spec, sheetName: unique }
    })
}

/** Build a fast lookup table from sheet name → spec. */
export function indexSpecsBySheetName(
  specs: ExcelSheetSpec[]
): Map<string, ExcelSheetSpec> {
  const index = new Map<string, ExcelSheetSpec>()
  for (const spec of specs) {
    index.set(spec.sheetName, spec)
  }
  return index
}
